import { useCallback, useMemo, useState, type ReactNode } from "react";
import { ToastContext, type ToastEntry, type ToastTone } from "@/components/feedback/toast-context";
import { cn } from "@/lib/cn";

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastEntry[]>([]);

  const push = useCallback((tone: ToastTone, message: string) => {
    const id = Date.now() + Math.random();
    setToasts((current) => [...current, { id, tone, message }]);
    window.setTimeout(() => {
      setToasts((current) => current.filter((toast) => toast.id !== id));
    }, 4200);
  }, []);

  const api = useMemo(
    () => ({
      success: (message: string) => push("success", message),
      error: (message: string) => push("error", message),
      info: (message: string) => push("info", message),
    }),
    [push],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="fixed inset-x-3 bottom-[calc(env(safe-area-inset-bottom)+0.75rem)] z-[80] flex flex-col gap-2 sm:inset-x-auto sm:right-4 sm:bottom-4 sm:w-[calc(100vw-2rem)] sm:max-w-sm sm:gap-3" aria-live="polite">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            role={toast.tone === "error" ? "alert" : "status"}
            className={cn(
              "rounded-md border bg-black/90 px-3 py-2 text-sm text-white shadow-panel backdrop-blur sm:px-4 sm:py-3",
              toast.tone === "success" && "border-teal-300/50",
              toast.tone === "error" && "border-red-300/60",
              toast.tone === "info" && "border-cyan-300/50",
            )}
          >
            {toast.message}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
